import { useState } from "react";
import { startExportJob } from "../lib/export/startExportJob.js";
import { DISPLAY } from "../constants.js";
import "./ExportDialog.css";

export default function ExportDialog({ config, entry, assetId, asset, doc, advanced, onClose, onExported }) {
  const selfHosted = doc.deliveryProfile === "self_hosted";
  const [motion, setMotion] = useState(doc.animationPreset);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [alreadyRunning, setAlreadyRunning] = useState(false);

  async function handleStart() {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await startExportJob(
        config,
        {
          brandId: entry.brand.id,
          assetId,
          action: "export",
          motionPreset: motion,
          asset,
          identity: entry.identity,
          references: entry.references,
          brand: entry.brand,
          userEmail: config.userEmail,
        },
        () => {},
        () => {}
      );
      if (result.exportJob?.alreadyRunning) {
        setAlreadyRunning(true);
      } else if (result.exportJob?.status === "processing") {
        setSubmitted(true);
      }
      onExported?.(result);
    } catch (err) {
      setSubmitError(err.message || "Couldn't start this export. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="export-dialog__overlay" role="dialog" aria-modal="true" aria-label="Export HTML5 banners">
      <div className="export-dialog">
        <div className="export-dialog__header">
          <h2 className="export-dialog__title">Export HTML5 zips</h2>
          <button type="button" onClick={onClose} aria-label="Close dialog" className="export-dialog__close">
            ✕
          </button>
        </div>
        <p className="export-dialog__subtitle">
          Validated for{" "}
          <span className="export-dialog__subtitle-strong">
            {selfHosted ? "self-hosted delivery" : "programmatic (Google / CM360)"}
          </span>
          {selfHosted ? " — infinite loops allowed." : " — ≤3 loops, ≤30s, held end frame."}
        </p>

        {alreadyRunning ? (
          <div className="export-dialog__body">
            <p className="export-dialog__submitted-message export-dialog__submitted-message--info">
              An export is already in progress for this asset — it'll finish shortly. You'll get
              an email at <strong>{config.userEmail}</strong> when it's ready; no need to start
              another one.
            </p>
            <div className="export-dialog__actions">
              <button type="button" onClick={onClose} className="export-dialog__export-btn">
                Done
              </button>
            </div>
          </div>
        ) : submitted ? (
          <div className="export-dialog__body">
            <p className="export-dialog__submitted-message">
              Building your export — this can take a few minutes for heavier assets. We'll email
              the results to <strong>{config.userEmail}</strong> as soon as it's ready.
            </p>
            <div className="export-dialog__actions">
              <button type="button" onClick={onClose} className="export-dialog__export-btn">
                Done
              </button>
            </div>
          </div>
        ) : (
          <div className="export-dialog__body">
            {advanced ? (
              <p className="export-dialog__motion-note">
                This banner uses <strong>per-element animations</strong> from the layout editor.
                Motion ships exactly as authored.
              </p>
            ) : (
              <label className="export-dialog__motion-field">
                <span className="export-dialog__motion-label">Motion for this export</span>
                <select
                  value={motion}
                  onChange={(e) => setMotion(e.target.value)}
                  disabled={submitting}
                  className="export-dialog__motion-select"
                >
                  {DISPLAY.ANIMATION_PRESETS.map((p) => (
                    <option key={p} value={p}>
                      {p.replace(/_/g, " ")}
                      {p === doc.animationPreset ? " (composer default)" : ""}
                    </option>
                  ))}
                </select>
                <span className="export-dialog__motion-hint">
                  Chosen per export — re-export anytime with a different motion. The banner itself
                  isn't changed.
                </span>
              </label>
            )}

            {submitError ? (
              <div className="export-dialog__error-row">
                <p className="export-dialog__error">{submitError}</p>
              </div>
            ) : null}

            <div>
              <p className="export-dialog__url-label">
                Click-through URL <span className="export-dialog__url-label-note">(clickTag — no UTMs baked in)</span>
              </p>
              <p className="export-dialog__url-value">{doc.clickUrl}</p>
            </div>

            <div className="export-dialog__actions">
              <button type="button" onClick={onClose} className="export-dialog__cancel-btn">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleStart}
                disabled={submitting}
                className="export-dialog__export-btn"
              >
                {submitting ? (
                  <>
                    <span className="export-dialog__spinner export-dialog__spinner--light" aria-hidden />
                    Starting…
                  </>
                ) : (
                  "Build & email me"
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}